/**
 * OrejaUpdatedTaskCard - Per-field inline editing for updated ClickUp task proposals.
 *
 * Each field value is directly clickable to edit inline. No global edit toggle.
 * Clicking to edit an unselected card auto-selects it. A brief green checkmark
 * flashes on save for visual feedback.
 *
 * @module OrejaUpdatedTaskCard
 */

import { useState, useRef, useEffect } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { X, ChevronDown, ArrowRight, Info, Check } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { scoreDescription, type DocQualityScore } from '@/lib/oreja/doc-quality-scorer';
import type { ProposalCardData } from '@/components/shared/ProposalCard';
import type { OrejaUpdatedTaskProposal } from '@/lib/oreja/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OrejaUpdatedTaskCardProps {
  /** The task update proposal. */
  proposal: OrejaUpdatedTaskProposal;
  /** Index in the proposals array. */
  index: number;
  /** Whether the card is selected for commit. */
  selected: boolean;
  /** Current description of the existing task (for diff display). */
  currentDescription?: string;
  /** Callback to toggle selection. */
  onToggle: (index: number, selected: boolean) => void;
  /** Callback to edit a simple field value (title, enhanced_description, comment_to_add). */
  onEdit: (index: number, field: string, value: string) => void;
  /** Callback to edit a single field change's "to" value (replaces JSON serialization). */
  onEditFieldChange?: (index: number, fieldChangeIndex: number, newToValue: string) => void;
  /** Callback to remove/deselect this proposal. */
  onRemove: (index: number) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns improvement tips based on doc quality sub-scores.
 *
 * @param score - Doc quality score breakdown
 * @returns Array of tip strings
 */
function getDocQualityTips(score: DocQualityScore): string[] {
  const tips: string[] = [];
  if (score.description_score < 20) tips.push('Add acceptance criteria or more detail');
  if (score.description_score < 30) tips.push('Include context about why this task matters');
  if (score.clarity_score < 10) tips.push('Mention current state or next steps');
  if (tips.length === 0) tips.push('Good documentation quality!');
  return tips;
}

/**
 * Returns confidence badge display properties.
 *
 * @param confidence - Confidence value 0-1
 * @returns Object with label and color class
 */
function getConfidenceBadge(confidence: number): { label: string; className: string } {
  if (confidence >= 0.8) return { label: 'High', className: 'bg-emerald-100 text-emerald-700' };
  if (confidence >= 0.5) return { label: 'Medium', className: 'bg-amber-100 text-amber-700' };
  return { label: 'Low', className: 'bg-red-100 text-red-700' };
}

/**
 * Maps a numeric 0-1 confidence to the ProposalCardData confidence level.
 *
 * @param confidence - AI confidence score 0-1
 * @returns ProposalCardData-compatible confidence label
 */
function toConfidenceLevel(confidence: number): ProposalCardData['confidence'] {
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.5) return 'medium';
  return 'low';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders a single updated task proposal card with per-field inline editing.
 *
 * Click any field value to edit it inline. Blur or Enter saves the change
 * and shows a brief green checkmark. No global edit button -- each field is
 * independently editable.
 *
 * @param props - Card properties
 * @returns A task update proposal card
 */
export function OrejaUpdatedTaskCard({
  proposal,
  index,
  selected,
  currentDescription = '',
  onToggle,
  onEdit,
  onEditFieldChange,
  onRemove,
}: OrejaUpdatedTaskCardProps) {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [savedField, setSavedField] = useState<string | null>(null);
  const [diffOpen, setDiffOpen] = useState(false);
  const [descriptionEditing, setDescriptionEditing] = useState(false);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  const conf = getConfidenceBadge(proposal.confidence);

  // Doc quality scores
  const beforeScore = scoreDescription(currentDescription, '');
  const afterDesc = proposal.enhanced_description || currentDescription;
  const afterScore = scoreDescription(afterDesc, proposal.comment_to_add || '');
  const delta = afterScore.score - beforeScore.score;

  // Build the ProposalCardData representation for type compatibility,
  // though we render the card ourselves due to Oreja-specific UI needs.
  const _cardData: ProposalCardData = {
    id: proposal.clickup_task_id,
    title: proposal.task_title,
    isNew: false,
    checked: selected,
    fieldChanges: proposal.field_changes.map((fc) => ({
      field: fc.field,
      from: fc.from,
      to: fc.to,
    })),
    descriptionBefore: currentDescription,
    descriptionAfter: proposal.enhanced_description,
    comment: proposal.comment_to_add,
    confidence: toConfidenceLevel(proposal.confidence),
    docQualityScore: Math.round(afterScore.score),
    docQualityDelta: Math.round(delta),
    docQualityTips: getDocQualityTips(afterScore),
    clickupTaskId: proposal.clickup_task_id,
  };

  /** Auto-select card if unselected when user clicks to edit. */
  function ensureSelected(): void {
    if (!selected) {
      onToggle(index, true);
    }
  }

  /** Flash green checkmark on save. */
  function flashSaved(fieldKey: string): void {
    setSavedField(fieldKey);
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setSavedField(null), 300);
  }

  /** Start editing a specific field. */
  function startEditing(fieldKey: string): void {
    ensureSelected();
    setEditingField(fieldKey);
  }

  /** Save a field change "to" value and close editing. */
  function saveFieldChange(fcIdx: number, newValue: string): void {
    if (onEditFieldChange) {
      onEditFieldChange(index, fcIdx, newValue);
    } else {
      // Fallback: use JSON serialization for backward compat
      const newChanges = [...proposal.field_changes];
      newChanges[fcIdx] = { ...newChanges[fcIdx], to: newValue };
      onEdit(index, 'field_changes', JSON.stringify(newChanges));
    }
    setEditingField(null);
    flashSaved(`fc-${fcIdx}`);
  }

  /** Save a simple field (title, comment). */
  function saveSimpleField(field: string, value: string): void {
    onEdit(index, field, value);
    setEditingField(null);
    flashSaved(field);
  }

  /** Handle key down on inline inputs (Enter to save, Escape to cancel). */
  function handleKeyDown(
    e: React.KeyboardEvent,
    onSave: () => void,
  ): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSave();
    } else if (e.key === 'Escape') {
      setEditingField(null);
    }
  }

  return (
    <div className={cn('rounded-lg border bg-card p-4 space-y-3 transition-opacity', !selected && 'opacity-60')}>
      {/* Header row */}
      <div className="flex items-start gap-2">
        <Checkbox
          checked={selected}
          onCheckedChange={(checked) => onToggle(index, !!checked)}
          className="mt-0.5"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {editingField === 'task_title' ? (
              <Input
                value={proposal.task_title}
                onChange={(e) => onEdit(index, 'task_title', e.target.value)}
                onBlur={() => saveSimpleField('task_title', proposal.task_title)}
                onKeyDown={(e) => handleKeyDown(e, () => saveSimpleField('task_title', proposal.task_title))}
                className="h-7 text-sm"
                autoFocus
              />
            ) : (
              <span
                className="text-sm font-medium truncate cursor-pointer hover:underline decoration-dashed underline-offset-2"
                onClick={() => startEditing('task_title')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') startEditing('task_title'); }}
                aria-label="Click to edit title"
              >
                {proposal.task_title}
              </span>
            )}
            {savedField === 'task_title' && (
              <Check className="h-3 w-3 text-emerald-500 animate-in fade-in duration-200" />
            )}
            <span className="text-[10px] text-muted-foreground/60 font-mono shrink-0">
              #{proposal.clickup_task_id}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 min-h-[44px] min-w-[44px]"
            onClick={() => onRemove(index)}
            aria-label="Remove proposal"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Badges row -- placed right after title for immediate visibility */}
      <div className="flex items-center gap-2 ml-6">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-xs text-muted-foreground cursor-help inline-flex items-center gap-1">
                Documentation: {Math.round(afterScore.score)}/100
                {delta !== 0 && (
                  <span className={cn('ml-1', delta > 0 ? 'text-emerald-600' : 'text-red-600')}>
                    ({delta > 0 ? '+' : ''}{Math.round(delta)})
                  </span>
                )}
                <Info className="h-3 w-3" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[240px]">
              <div className="space-y-1 text-xs">
                <p className="font-medium">Score breakdown:</p>
                <p>Description: {afterScore.description_score}/40</p>
                <p>Clarity: {afterScore.clarity_score}/30</p>
                <p>Comments: {afterScore.comment_score}/30</p>
                {getDocQualityTips(afterScore).map((tip, i) => (
                  <p key={i} className="text-muted-foreground">{'\u2022'} {tip}</p>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <span className={cn('text-xs rounded px-1.5 py-0.5', conf.className)}>
          Confidence: {conf.label}
        </span>
      </div>

      {/* Field changes */}
      <div className="ml-6 space-y-1.5">
        {proposal.field_changes.map((fc, fcIdx) => (
          <div key={fcIdx} className="flex items-center gap-1.5 text-xs">
            <span className="font-medium capitalize shrink-0 w-16">{fc.field}:</span>
            <span className="text-muted-foreground">{fc.from ?? '(none)'}</span>
            <ArrowRight className="h-3 w-3 shrink-0" />
            {editingField === `fc-${fcIdx}` ? (
              <Input
                type={fc.field === 'due_date' || fc.field === 'start_date' ? 'date' : 'text'}
                defaultValue={fc.to ?? ''}
                onBlur={(e) => saveFieldChange(fcIdx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, () => {
                  const input = e.target as HTMLInputElement;
                  saveFieldChange(fcIdx, input.value);
                })}
                className="h-6 text-xs flex-1"
                autoFocus
              />
            ) : (
              <span
                className="font-medium cursor-pointer border-b border-dashed border-muted-foreground/40 hover:border-foreground"
                onClick={() => startEditing(`fc-${fcIdx}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') startEditing(`fc-${fcIdx}`); }}
                aria-label={`Click to edit ${fc.field} value`}
              >
                {fc.to ?? '(none)'}
              </span>
            )}
            {savedField === `fc-${fcIdx}` && (
              <Check className="h-3 w-3 text-emerald-500 animate-in fade-in duration-200" />
            )}
          </div>
        ))}
      </div>

      {/* Enhanced description */}
      {(proposal.enhanced_description || descriptionEditing) && (
        <div className="ml-6 space-y-1">
          {descriptionEditing ? (
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wide">Description</label>
              <Textarea
                value={proposal.enhanced_description ?? ''}
                onChange={(e) => onEdit(index, 'enhanced_description', e.target.value)}
                onBlur={() => {
                  setDescriptionEditing(false);
                  flashSaved('enhanced_description');
                }}
                className="text-xs min-h-[80px]"
                placeholder="Enhanced task description..."
                autoFocus
              />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Collapsible open={diffOpen} onOpenChange={setDiffOpen}>
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <ChevronDown className={cn('h-3 w-3 transition-transform', diffOpen && 'rotate-180')} />
                    Description changes
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-1 space-y-1">
                  {currentDescription && (
                    <div className="rounded bg-muted/50 p-2 text-xs whitespace-pre-wrap max-h-48 overflow-y-auto">
                      <span className="text-muted-foreground font-medium">Before:</span>
                      <br />
                      {currentDescription}
                    </div>
                  )}
                  <div className="rounded bg-emerald-50 dark:bg-emerald-950/20 p-2 text-xs whitespace-pre-wrap max-h-48 overflow-y-auto">
                    <span className="font-medium">{currentDescription ? 'After:' : 'New description:'}</span>
                    <br />
                    {proposal.enhanced_description}
                  </div>
                </CollapsibleContent>
              </Collapsible>
              <button
                type="button"
                className="text-[10px] text-muted-foreground hover:text-foreground underline"
                onClick={() => {
                  ensureSelected();
                  setDescriptionEditing(true);
                }}
              >
                Edit
              </button>
              {savedField === 'enhanced_description' && (
                <Check className="h-3 w-3 text-emerald-500 animate-in fade-in duration-200" />
              )}
            </div>
          )}
        </div>
      )}

      {/* Comment to add */}
      {(proposal.comment_to_add || editingField === 'comment_to_add') && (
        <div className="ml-6">
          {editingField === 'comment_to_add' ? (
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wide">Comment to Add</label>
              <Textarea
                value={proposal.comment_to_add ?? ''}
                onChange={(e) => onEdit(index, 'comment_to_add', e.target.value)}
                onBlur={() => saveSimpleField('comment_to_add', proposal.comment_to_add ?? '')}
                className="text-xs min-h-[40px]"
                placeholder="Comment to post on the task..."
                autoFocus
              />
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <div
                className="border-l-2 border-muted pl-2 text-xs text-muted-foreground italic cursor-pointer hover:text-foreground flex-1"
                onClick={() => startEditing('comment_to_add')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') startEditing('comment_to_add'); }}
                aria-label="Click to edit comment"
              >
                {proposal.comment_to_add}
              </div>
              {savedField === 'comment_to_add' && (
                <Check className="h-3 w-3 text-emerald-500 animate-in fade-in duration-200" />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
